// Epic 33 — pure, immutable editing transforms for a week plan (array of
// WeekPlanEmployeeDTO). Extracted from WeekPlanContent so the Week Plan page and
// the Summary "edit week" modal share ONE source of truth for shift/time editing
// (esp. the UTC day-anchoring that keeps a shift's start/end on the same date).

import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { getDateFromWeekAndDay } from "../utils/weekPlanUtils";

dayjs.extend(utc);
dayjs.extend(customParseFormat);

// Build a work duration anchored to the ACTUAL date of this week-day so start
// and end share the correct day (a "today"-based build could split them across
// dates and make the hours total ignore the section).
export function createDefaultWorkDuration(day, year, week, startTime = "08:00", endTime = "12:00") {
  const defaultDate = dayjs(getDateFromWeekAndDay(year, week, day)).format("YYYY-MM-DD");
  const startLocal = dayjs(`${defaultDate} ${startTime}`, "YYYY-MM-DD HH:mm");
  let endLocal = dayjs(`${defaultDate} ${endTime}`, "YYYY-MM-DD HH:mm");
  if (!endLocal.isValid() || !endLocal.isAfter(startLocal)) {
    endLocal = startLocal.add(1, "hour");
  }
  return {
    id: 0,
    startTime: dayjs.utc(startLocal.valueOf()).toDate(),
    endTime: dayjs.utc(endLocal.valueOf()).toDate(),
  };
}

// Suggest the next free slot: 1h after the latest end, 4h long, clamped to the day.
export function suggestNextTimeSlot(existingDurations) {
  if (!existingDurations || existingDurations.length === 0) {
    return { startTime: "08:00", endTime: "12:00" };
  }
  const sorted = existingDurations
    .filter((d) => d.startTime && d.endTime)
    .sort((a, b) => dayjs(a.startTime).valueOf() - dayjs(b.startTime).valueOf());
  if (sorted.length === 0) return { startTime: "08:00", endTime: "12:00" };

  const lastEnd = dayjs(sorted[sorted.length - 1].endTime);
  const suggestedStart = lastEnd.add(1, "hour");
  let suggestedEnd = suggestedStart.add(4, "hour");
  if (suggestedEnd.format("YYYY-MM-DD") !== suggestedStart.format("YYYY-MM-DD")) {
    suggestedEnd = suggestedStart.endOf("day");
  }
  return { startTime: suggestedStart.format("HH:mm"), endTime: suggestedEnd.format("HH:mm") };
}

// --- immutable transforms on the plans array (returns a new array) ---

// Map over calendarEmployees → dailyPlanEmployees for a matching user's plan.
function mapUserDailyPlans(plans, userId, mapDailyPlan) {
  return plans.map((plan) => {
    if (plan.userId !== userId) return plan;
    return {
      ...plan,
      calendarEmployees: plan.calendarEmployees.map((calendar) => ({
        ...calendar,
        dailyPlanEmployees: calendar.dailyPlanEmployees.map(mapDailyPlan),
      })),
    };
  });
}

export function setWorkDurationTime(plans, userId, day, durationIndex, type, timeString, year, week) {
  return mapUserDailyPlans(plans, userId, (dailyPlan) => {
    if (dailyPlan.day !== day) return dailyPlan;
    return {
      ...dailyPlan,
      workDurationEmployees: dailyPlan.workDurationEmployees.map((duration, index) => {
        if (index !== durationIndex) return duration;
        let newDate = duration[type];
        if (timeString) {
          const dayDate = dayjs(getDateFromWeekAndDay(year, week, day)).format("YYYY-MM-DD");
          const localDateTime = dayjs(`${dayDate} ${timeString}`, "YYYY-MM-DD HH:mm");
          if (localDateTime.isValid()) {
            newDate = dayjs.utc(localDateTime.valueOf()).toDate();
          }
        }
        return { ...duration, [type]: newDate };
      }),
    };
  });
}

export function setDayStatus(plans, userId, day, newStatus) {
  return mapUserDailyPlans(plans, userId, (dp) =>
    dp.day === day ? { ...dp, dayStatus: newStatus } : dp
  );
}

export function setComment(plans, userId, day, comment) {
  return mapUserDailyPlans(plans, userId, (dp) => (dp.day === day ? { ...dp, comment } : dp));
}

export function setSubOrganisation(plans, userId, day, subOrganisationId, subOrganisations = []) {
  return mapUserDailyPlans(plans, userId, (dp) => {
    if (dp.day !== day) return dp;
    const selected = subOrganisations.find((so) => so.id === subOrganisationId);
    return { ...dp, subOrganisationId: subOrganisationId || null, subOrganisation: selected || null };
  });
}

// Appends a suggested work duration to the day. Pure transform — callers handle
// validation/UX messaging.
export function addWorkDuration(plans, userId, day, year, week) {
  return mapUserDailyPlans(plans, userId, (dailyPlan) => {
    if (dailyPlan.day !== day) return dailyPlan;
    const existing = dailyPlan.workDurationEmployees || [];
    const { startTime, endTime } = suggestNextTimeSlot(existing);
    const newDuration = createDefaultWorkDuration(day, year, week, startTime, endTime);
    return { ...dailyPlan, workDurationEmployees: [...existing, newDuration] };
  });
}

// Removes the duration at durationIndex. Pure transform — callers guard the
// "cannot delete the last duration" rule.
export function removeWorkDuration(plans, userId, day, durationIndex) {
  return mapUserDailyPlans(plans, userId, (dailyPlan) => {
    if (dailyPlan.day !== day) return dailyPlan;
    const existing = dailyPlan.workDurationEmployees || [];
    return { ...dailyPlan, workDurationEmployees: existing.filter((_, idx) => idx !== durationIndex) };
  });
}

// --- validation ---

// Returns the shift errors on a single WeekPlanEmployeeDTO: any duration whose
// end is not strictly after its start, and any overlapping durations within a
// day. Used to gate Save so an invalid edit can't be persisted.
export function getShiftValidationErrors(plan) {
  const errors = [];
  if (!plan) return errors;
  (plan.calendarEmployees || []).forEach((cal) => {
    (cal.dailyPlanEmployees || []).forEach((dp) => {
      const spans = (dp.workDurationEmployees || [])
        .filter((d) => d.startTime && d.endTime)
        .map((d) => ({ start: dayjs(d.startTime).valueOf(), end: dayjs(d.endTime).valueOf() }));
      spans.forEach((s) => {
        if (!(s.end > s.start)) errors.push({ day: dp.day, reason: "range" });
      });
      const sorted = [...spans].sort((a, b) => a.start - b.start);
      for (let i = 1; i < sorted.length; i++) {
        if (sorted[i].start < sorted[i - 1].end) errors.push({ day: dp.day, reason: "overlap" });
      }
    });
  });
  return errors;
}

export function hasShiftErrors(plan) {
  return getShiftValidationErrors(plan).length > 0;
}
